import EmailLink from '@/components/ui/EmailLink.tsx';
import {
  CLUB_PRO_ANNUAL_TTC_CENTS,
  CLUB_PRO_MONTHLY_TTC_CENTS,
  COMMISSION_FIXED_FEE_CENTS,
  COMMISSION_MIN_FEE_CENTS,
  COMMISSION_MONTHLY_LIMIT,
  COMMISSION_RATE_BPS,
  COMMISSION_YEARLY_LIMIT,
  ORGA_FORFAIT_ANNUAL_TTC_CENTS,
  ORGA_FORFAIT_MONTHLY_TTC_CENTS,
} from '@shared/constants';

// Libellé légal de la commission, dérivé des constantes partagées.
const fmtEur = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
const COMMISSION_FULL_LABEL = `${COMMISSION_RATE_BPS / 100} % du montant TTC de l'inscription + ${fmtEur(
  COMMISSION_FIXED_FEE_CENTS,
)}, avec un minimum de ${fmtEur(COMMISSION_MIN_FEE_CENTS)} par inscription (TVA incluse)`;
const COMMISSION_SHORT_LABEL = `${COMMISSION_RATE_BPS / 100} % + ${fmtEur(COMMISSION_FIXED_FEE_CENTS)}, min ${fmtEur(
  COMMISSION_MIN_FEE_CENTS,
)}`;

// Limite réelle du plan Commission Partenaire, appliquée en dur par l'API
// (competitions.service.ts). Doit rester strictement alignée avec la page Tarifs.
const COMMISSION_LIMITS_LABEL = `${COMMISSION_MONTHLY_LIMIT} concours payants par mois et ${COMMISSION_YEARLY_LIMIT} par an`;

export default function CguSections1() {
  return (
    <>
      {/* 1. Objet et éditeur */}
      <div id='objet' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>1. Objet et éditeur</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            Les présentes Conditions Générales d'Utilisation et de Vente (ci-après « CGU/CGV ») régissent l'accès et
            l'utilisation de la plateforme <strong className='text-slate-900'>BoulesConnect</strong> (ci-après « la
            Plateforme »), service SaaS de gestion de concours de pétanque, jeu provençal et boule lyonnaise accessible
            à l'adresse <strong className='text-slate-900'>boulesconnect.fr</strong>.
          </p>
          <p>La Plateforme est éditée et exploitée par la société :</p>
          <div className='p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1'>
            <p>
              <strong className='text-slate-900'>MADSAAS SAS</strong>
            </p>
            <p>Société par actions simplifiée au capital social de 1 000 €</p>
            <p>SIREN : 821 350 600 — SIRET siège : 821 350 600 00036</p>
            <p>Numéro de TVA intracommunautaire : FR00821350600</p>
            <p>Code APE/NAF : 6202A — Conseil en systèmes et logiciels informatiques</p>
            <p>Siège social : 05110 Lardier-et-Valença, France</p>
            <p>Immatriculée au RCS de Gap</p>
            <p>Directeur de la publication : Marc-Antoine DUTOIT</p>
          </div>
          <p>La Plateforme est hébergée par :</p>
          <div className='p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1'>
            <p>
              <strong className='text-slate-900'>OVH SAS</strong>
            </p>
            <p>2 rue Kellermann, 59100 Roubaix, France</p>
            <p>Téléphone : +33 9 72 10 10 07</p>
            <p>Site : ovhcloud.com</p>
            <p>Datacenters : Roubaix et Gravelines (France) — certifiés ISO 27001 et ISO 27701.</p>
          </div>
          <p>
            L'inscription et l'utilisation de la Plateforme valent acceptation pleine et entière des présentes CGU/CGV.
            Ces conditions sont accessibles et imprimables à tout moment via un lien permanent en bas de chaque page de
            la Plateforme.
          </p>
        </div>
      </div>

      {/* 2. Définitions */}
      <div id='definitions' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>2. Définitions</h2>
        <div className='text-sm text-slate-600 leading-relaxed'>
          <ul className='space-y-2.5'>
            <li>
              <strong className='text-slate-900'>Éditeur</strong> : MADSAAS SAS, société éditrice de la Plateforme
              BoulesConnect.
            </li>
            <li>
              <strong className='text-slate-900'>Plateforme</strong> : le site web boulesconnect.fr, son API et
              l'ensemble des services associés (ci-après « BoulesConnect »).
            </li>
            <li>
              <strong className='text-slate-900'>Utilisateur</strong> : toute personne physique ou morale accédant à la
              Plateforme et utilisant ses services, qu'elle soit inscrite ou non.
            </li>
            <li>
              <strong className='text-slate-900'>Joueur</strong> : Utilisateur inscrit en qualité de participant aux
              concours. Peut bénéficier du plan <em>Joueur Free</em> (gratuit) ou <em>Joueur Plus</em>.
            </li>
            <li>
              <strong className='text-slate-900'>Club</strong> : Utilisateur inscrit en qualité d'association ou club
              sportif. Peut bénéficier du plan <em>Club Free</em> (gratuit) ou <em>Club Pro</em>.
            </li>
            <li>
              <strong className='text-slate-900'>Organisateur</strong> : Utilisateur inscrit en qualité d'organisateur
              de concours, titulaire d'une organisation sur la Plateforme. Il relève au choix du plan{' '}
              <em>Commission Partenaire</em> (0 € fixe · {COMMISSION_SHORT_LABEL} de commission par inscription payée ·
              concours <strong className='text-slate-900'>gratuits</strong> illimités ·{' '}
              <strong className='text-slate-900'>{COMMISSION_LIMITS_LABEL}</strong>) ou du plan <em>Forfait</em>{' '}
              (abonnement mensuel ou annuel · 0 % de commission · concours payants illimités).
            </li>
            <li>
              <strong className='text-slate-900'>Concours</strong> : compétition de pétanque, de jeu provençal ou de
              boule lyonnaise créée et gérée via la Plateforme par un Organisateur.
            </li>
            <li>
              <strong className='text-slate-900'>Inscription</strong> : acte par lequel un Joueur s'inscrit, seul ou en
              équipe, à un Concours via la Plateforme, avec ou sans paiement de droits d'entrée.
            </li>
            <li>
              <strong className='text-slate-900'>Commission</strong> : frais de service prélevés par l'Éditeur sur
              chaque inscription payante traitée via la Plateforme.
            </li>
            <li>
              <strong className='text-slate-900'>Stripe Connect</strong> : service de paiement de Stripe permettant à un
              Organisateur de recevoir directement les droits d'inscription sur son propre compte Stripe, déduction
              faite de la Commission.
            </li>
            <li>
              <strong className='text-slate-900'>Abonnement</strong> : contrat à exécution successive souscrit par un
              Utilisateur pour accéder aux fonctionnalités d'un plan payant.
            </li>
          </ul>
        </div>
      </div>

      {/* 3. Inscription et compte */}
      <div id='inscription' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>3. Inscription et compte</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            L'accès aux fonctionnalités essentielles de la Plateforme nécessite la création d'un compte utilisateur.
            L'inscription est gratuite et ouverte à toute personne physique âgée d'au moins{' '}
            <strong className='text-slate-900'>15 ans</strong> ou à toute personne morale légalement constituée.
          </p>
          <p>
            L'Utilisateur s'engage à fournir des informations exactes, complètes et à jour. La vérification de l'adresse
            email est obligatoire pour accéder à certaines fonctionnalités (notamment les fonctionnalités Organisateur).
            Un seul compte par personne physique est autorisé.
          </p>
          <p>
            L'Utilisateur est seul responsable de la confidentialité de ses identifiants et de toute activité réalisée
            depuis son compte. En cas de compromission suspectée, il doit en informer immédiatement l'Éditeur à{' '}
            <EmailLink user='support' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />.
          </p>
          <p>
            L'Éditeur se réserve le droit de suspendre ou supprimer tout compte en cas de violation des présentes
            CGU/CGV, d'informations frauduleuses ou de comportement contraire aux intérêts de la communauté, sans
            préavis ni indemnité, sous réserve des droits des abonnés définis à l'article 13.
          </p>
        </div>
      </div>

      {/* 4. Services et plans */}
      <div id='services' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>4. Services et plans</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-5'>
          <p>La Plateforme propose des services différenciés selon le profil et le plan souscrit :</p>

          {/* Joueur */}
          <div>
            <h3 className='font-bold text-slate-900 mb-3'>4.1 Joueur</h3>
            <div className='grid md:grid-cols-2 gap-3'>
              <div className='p-4 bg-blue-50 rounded-2xl border border-blue-100'>
                <p className='font-bold text-blue-900 mb-2'>Joueur Free — Gratuit</p>
                <ul className='space-y-1 text-blue-800 text-xs'>
                  <li>- Création et gestion de profil joueur</li>
                  <li>- Recherche de concours (filtres, géolocalisation)</li>
                  <li>- Inscription aux concours (gratuits ou payants)</li>
                  <li>- Consultation des résultats et classements</li>
                  <li>- Accès à l'annuaire des clubs</li>
                  <li>- Demande d'adhésion à un club</li>
                </ul>
              </div>
              {/*<div className='p-4 bg-blue-100 rounded-2xl border border-blue-200'>*/}
              {/*  <p className='font-bold text-blue-900 mb-2'>*/}
              {/*    Joueur Plus — {JOUEUR_PLUS_MONTHLY_TTC_CENTS / 100} € TTC/mois ({JOUEUR_PLUS_ANNUAL_TTC_CENTS / 100} €*/}
              {/*    TTC/an)*/}
              {/*  </p>*/}
              {/*  <ul className='space-y-1 text-blue-800 text-xs'>*/}
              {/*    <li>- Toutes les fonctionnalités Free</li>*/}
              {/*    <li>- Expérience sans publicité</li>*/}
              {/*    <li>- Historique complet et exports avancés</li>*/}
              {/*    <li>- Accès prioritaire aux fonctionnalités futures</li>*/}
              {/*  </ul>*/}
              {/*</div>*/}
            </div>
          </div>

          {/* Club */}
          <div>
            <h3 className='font-bold text-slate-900 mb-3'>4.2 Club</h3>
            <div className='grid md:grid-cols-2 gap-3'>
              <div className='p-4 bg-emerald-50 rounded-2xl border border-emerald-100'>
                <p className='font-bold text-emerald-900 mb-2'>Club Free — Gratuit</p>
                <ul className='space-y-1 text-emerald-800 text-xs'>
                  <li>- Fiche club publique géolocalisée (logo, coordonnées)</li>
                  <li>- Gestion des membres et des licences</li>
                  <li>- Workflow de demandes d'adhésion en ligne</li>
                  <li>- Gestion des équipes et inscriptions groupées</li>
                  <li>- Affiliation à une organisation organisatrice</li>
                </ul>
              </div>
              <div className='p-4 bg-emerald-100 rounded-2xl border border-emerald-200'>
                <p className='font-bold text-emerald-900 mb-2'>
                  Club Pro — {CLUB_PRO_MONTHLY_TTC_CENTS / 100} € TTC/mois ({CLUB_PRO_ANNUAL_TTC_CENTS / 100} € TTC/an)
                </p>
                <ul className='space-y-1 text-emerald-800 text-xs'>
                  <li>- Toutes les fonctionnalités Free</li>
                  <li>- Blog du club (rédaction et publication d'articles sur la fiche publique)</li>
                  <li>- Exports CSV avancés (membres, résultats)</li>
                  <li>- Statistiques de participation</li>
                  <li>- Accès prioritaire aux nouvelles fonctionnalités</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Organisateur */}
          <div>
            <h3 className='font-bold text-slate-900 mb-3'>4.3 Organisateur</h3>
            <div className='grid md:grid-cols-2 gap-3'>
              <div className='p-4 bg-violet-50 rounded-2xl border border-violet-100'>
                <p className='font-bold text-violet-900 mb-2'>Commission Partenaire — 0 € fixe</p>
                <ul className='space-y-1 text-violet-800 text-xs'>
                  <li>- Concours gratuits illimités (aucun frais, aucune commission)</li>
                  <li>
                    - <strong>Concours payants limités à {COMMISSION_LIMITS_LABEL}</strong>. Au-delà, le passage au plan
                    Forfait est requis.
                  </li>
                  <li>- Création et gestion complète des concours</li>
                  <li>- Gestion des inscriptions et des équipes</li>
                  <li>- Encaissement des droits via Stripe Connect</li>
                  <li>- Saisie des résultats et publication du podium</li>
                  <li>- Export CSV inscriptions et résultats</li>
                  <li>
                    - <strong>Commission BoulesConnect : {COMMISSION_FULL_LABEL}</strong>. Exemple : une inscription à
                    10 € donne une commission de {fmtEur(COMMISSION_MIN_FEE_CENTS)} (le minimum) ; une inscription à 50
                    € donne une commission de 1,30 €.
                  </li>
                </ul>
              </div>
              <div className='p-4 bg-violet-100 rounded-2xl border border-violet-200'>
                <p className='font-bold text-violet-900 mb-2'>
                  Forfait — {ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100} € TTC/mois ({ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100} €
                  TTC/an)
                </p>
                <ul className='space-y-1 text-violet-800 text-xs'>
                  <li>- Toutes les fonctionnalités du plan Commission Partenaire</li>
                  <li>- 0 % de commission sur les inscriptions payantes</li>
                  <li>- Concours payants illimités</li>
                  <li>- Blog de l'organisateur (rédaction et publication d'articles sur la fiche publique)</li>
                  <li>- Support prioritaire</li>
                </ul>
              </div>
            </div>
            <p className='mt-3 text-xs text-slate-500'>
              Les tarifs Club Pro et Forfait organisateur sont exprimés toutes taxes comprises (TTC). La Commission
              Partenaire ({COMMISSION_SHORT_LABEL} par inscription) est prélevée sur chaque inscription payée via Stripe
              Connect. Cette grille de commission s'applique aux concours payants depuis le 12 juin 2026. Le décompte
              des concours payants du plan Commission Partenaire s'effectue sur le fuseau horaire Europe/Paris, à la
              date de début du concours.
            </p>
          </div>
        </div>
      </div>

      {/* 5. Tarifs et facturation */}
      <div id='tarifs' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>5. Tarifs et facturation</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            Les abonnements payants (Club Pro, Forfait organisateur, et le cas échéant Joueur Plus lorsque cette offre
            est proposée) sont facturés d'avance, mensuellement ou annuellement selon l'option choisie à la
            souscription. Le paiement est effectué par carte bancaire via Stripe, prestataire de services de paiement
            (Stripe Payments Europe Limited, agréé par la Banque Centrale d'Irlande en tant qu'établissement de monnaie
            électronique).
          </p>
          <p>
            L'abonnement est renouvelé automatiquement à sa date anniversaire. L'Utilisateur peut désactiver le
            renouvellement automatique depuis son espace personnel ou via le portail de facturation Stripe, la
            désactivation prenant effet à la fin de la période en cours déjà payée.
          </p>
          <p>
            La commission applicable au plan Commission Partenaire ({COMMISSION_SHORT_LABEL} par inscription) est
            prélevée automatiquement sur chaque transaction d'inscription payante, au moment du paiement par le Joueur,
            via le mécanisme Stripe Connect (voir article 6). Aucun virement ni prélèvement séparé n'est effectué à ce
            titre sur le compte de l'Organisateur.
          </p>
          <p>
            Des justificatifs de paiement (reçus Stripe) sont disponibles dans l'espace personnel de l'Utilisateur. Les
            factures TVA pour les abonnements payants sont émises par MADSAAS SAS et adressées par email à chaque
            période de facturation. Les factures sont conservées pendant 10 ans conformément à l'article L.123-22 du
            Code de commerce.
          </p>
          <p>
            L'Éditeur se réserve le droit de modifier ses tarifs. Toute modification tarifaire sera notifiée aux abonnés
            concernés par email au moins 30 jours avant son entrée en vigueur. En cas de désaccord avec la modification,
            l'abonné peut résilier son abonnement sans frais avant l'entrée en vigueur du nouveau tarif ; à défaut de
            résiliation, la modification s'appliquera aux périodes suivantes.
          </p>
        </div>
      </div>

      {/* 6. Stripe Connect */}
      <div id='stripe-connect' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>6. Paiements des inscriptions via Stripe Connect</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            Pour les Concours à participation payante, BoulesConnect utilise le service{' '}
            <strong className='text-slate-900'>Stripe Connect Standard</strong>, fourni par Stripe Payments Europe
            Limited (Irlande) / Stripe, Inc. (États-Unis). Ce mécanisme opère un règlement direct (Direct Charge) entre
            le Joueur inscrit et l'Organisateur, sans encaissement des droits d'inscription par MADSAAS SAS, les
            paiements étant traités via Stripe Connect au bénéfice de l'Organisateur.
          </p>
          <p>
            <strong className='text-slate-900'>Flux financier :</strong> Le Joueur règle le montant total des droits
            d'inscription. Le montant net (droits − commission BoulesConnect − frais Stripe) est crédité sur le compte
            Stripe de l'Organisateur, dans les délais habituels de règlement Stripe (généralement 2 à 7 jours ouvrés),
            sous réserve des mesures de gestion des risques pouvant être appliquées par Stripe.
          </p>
          <p>
            <strong className='text-slate-900'>Conditions préalables :</strong> Pour accepter des paiements,
            l'Organisateur doit créer et valider un compte Stripe Connect Standard et accepter directement les
            conditions générales de Stripe. La vérification d'identité (KYC) est réalisée par Stripe, conformément à la
            réglementation anti-blanchiment (LCB-FT, articles L.561-5 et L.561-12 du Code monétaire et financier).
            L'activation du compte Stripe est sous la responsabilité exclusive de Stripe ; BoulesConnect n'est pas
            partie au contrat conclu entre l'Organisateur et Stripe.
          </p>
          <p>
            <strong className='text-slate-900'>Mesures de gestion des risques Stripe :</strong> L'Utilisateur reconnaît
            que Stripe peut, à tout moment et conformément à ses propres conditions, appliquer des mesures de gestion
            des risques et de conformité, notamment (sans limitation) : la demande d'informations ou documents
            complémentaires, la mise en réserve de fonds, le blocage, la limitation ou le retard de certains paiements
            et/ou versements (<em>payout holds</em>), ainsi que la suspension ou la clôture d'un compte Stripe Connect.
          </p>
          <p>
            <strong className='text-slate-900'>Litiges, rétrofacturations et soldes négatifs :</strong> En cas de
            litige, contestation, rétrofacturation (<em>chargeback</em>), fraude ou remboursement, des montants (et
            frais associés) peuvent être débités du compte Stripe Connect de l'Organisateur et entraîner un solde
            négatif. L'Organisateur s'oblige à maintenir des fonds suffisants pour couvrir ces montants.
          </p>
          <p>
            <strong className='text-slate-900'>Conséquences pour la Plateforme :</strong> L'Utilisateur reconnaît qu'au
            regard des mécanismes contractuels et de la configuration Stripe Connect applicables, Stripe peut exiger la
            mise en place de mesures de prévention et/ou constituer des réserves et/ou procéder à des compensations sur
            le compte Stripe de la Plateforme afin de couvrir des expositions au risque liées aux comptes connectés.
          </p>
          <p>
            <strong className='text-slate-900'>Pouvoirs de suspension :</strong> En cas de risque avéré ou suspecté
            (taux de litiges anormal, fraude, défaut de remboursement, informations inexactes, non-conformité), MADSAAS
            SAS se réserve le droit, à sa seule discrétion et sans indemnité, de suspendre temporairement l'accès de
            l'Organisateur aux fonctionnalités de paiement et/ou de désactiver les paiements pour les Concours
            concernés, le temps des vérifications nécessaires.
          </p>
          <p>
            <strong className='text-slate-900'>Responsabilité de l'Organisateur :</strong> L'Organisateur est
            responsable de l'exactitude des informations communiquées à Stripe, du respect de la réglementation fiscale
            applicable aux sommes perçues, et de la politique de remboursement de son Concours. BoulesConnect n'est pas
            responsable des retards, restrictions, suspensions ou blocages de paiement/versement décidés par Stripe.
          </p>
          <p>
            Pour les Concours gratuits (prix = 0 €), aucun flux financier n'est traité via Stripe Connect et aucune
            commission n'est due.
          </p>
        </div>
      </div>

      {/* 7. Droit de rétractation */}
      <div id='retractation' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>7. Droit de rétractation</h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            Le présent article distingue deux régimes de rétractation, applicables selon la nature du contrat conclu sur
            la Plateforme. Le droit de rétractation ne bénéficie qu'aux{' '}
            <strong className='text-slate-900'>
              consommateurs personnes physiques agissant hors de toute activité professionnelle
            </strong>{' '}
            (article L.221-3 du Code de la consommation). Il ne s'applique pas aux personnes morales (associations,
            clubs, sociétés) ni aux Organisateurs agissant dans le cadre de leur activité professionnelle.
          </p>
          <p className='pt-2'>
            <strong className='text-slate-900 text-base'>
              7.1 Abonnements payants (Club Pro, Forfait organisateur, Joueur Plus)
            </strong>
          </p>
          <p>
            Conformément aux articles L.221-18 à L.221-28 du Code de la consommation, le consommateur dispose d'un délai
            de <strong className='text-slate-900'>14 jours calendaires</strong> à compter de la souscription pour
            exercer son droit de rétractation, sans avoir à justifier de motifs ni à payer de pénalités. En cas
            d'exercice valide, l'Éditeur procède au remboursement intégral des sommes versées dans un délai de 14 jours
            à compter de la réception de la demande, par le même moyen de paiement que celui utilisé lors de la
            souscription.
          </p>
          <p>
            <strong className='text-slate-900'>Demande d'exécution immédiate (option) :</strong> si, lors de la
            souscription, le consommateur demande expressément que l'exécution du service débute avant l'expiration du
            délai de rétractation et reconnaît expressément perdre son droit de rétractation au terme de la pleine
            exécution du contrat, il en sera tenu compte conformément aux articles L.221-25 et L.221-28 1° du Code de la
            consommation. Pour les services numériques sans support matériel, l'article L.221-28 13° s'applique dans les
            mêmes conditions (consentement préalable exprès et reconnaissance expresse de la perte du droit de
            rétractation au début de l'exécution).
          </p>
          <p>
            Pour exercer ce droit, le consommateur adresse sa demande par email à{' '}
            <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' /> ou par courrier
            au siège social de MADSAAS SAS, en indiquant ses coordonnées, le service concerné et la date de
            souscription. Toute déclaration dépourvue d'ambiguïté est suffisante ; l'utilisation du formulaire-type
            ci-dessous n'est pas obligatoire.
          </p>
          <div className='p-5 bg-blue-50 rounded-2xl border border-blue-100'>
            <p className='font-bold text-blue-900 mb-2'>
              Modèle de formulaire de rétractation (article R.221-1 du Code de la consommation)
            </p>
            <pre className='text-xs text-blue-900 whitespace-pre-wrap font-mono'>
              {`À l'attention de MADSAAS SAS — BoulesConnect
05110 Lardier-et-Valença, France
Email : dpo@boulesconnect.fr

Je vous notifie par la présente ma rétractation du contrat portant
sur la prestation de services ci-dessous :

— Service concerné : [à préciser]
— Date de souscription : [JJ/MM/AAAA]
— Nom du consommateur : [Prénom NOM]
— Adresse du consommateur : [adresse postale]
— Email du consommateur : [email]

Date : [JJ/MM/AAAA]
Signature (si notification papier) :`}
            </pre>
          </div>
          <p className='pt-3'>
            <strong className='text-slate-900 text-base'>
              7.2 Inscription à un Concours — absence de droit de rétractation
            </strong>
          </p>
          <p>
            L'inscription à un Concours payant constitue un contrat conclu à distance ayant pour objet une prestation d'
            <strong className='text-slate-900'>activité de loisirs</strong> (compétition sportive de pétanque, jeu
            provençal ou boule lyonnaise){' '}
            <strong className='text-slate-900'>fournie à une date ou pour une période déterminée</strong> (la date du
            Concours).
          </p>
          <p>
            À ce titre, conformément aux dispositions impératives de{' '}
            <strong className='text-slate-900'>l'article L.221-28 12° du Code de la consommation</strong>, le Joueur ne
            dispose d'aucun droit de rétractation au titre de cette inscription, dès la conclusion du contrat. Cette
            exclusion est de plein droit, automatique, et ne nécessite ni renonciation expresse ni case à cocher dédiée
            du consommateur. Elle est confirmée par la jurisprudence de la Cour de Justice de l'Union Européenne (CJUE,
            31 mars 2022, aff. C-96/21, relative aux billets pour événements de loisirs vendus à distance).
          </p>
          <p>
            Le Joueur reconnaît expressément avoir été informé, préalablement à l'inscription, de l'absence de droit de
            rétractation pour ce type de contrat, conformément aux articles L.221-5 et L.221-28 du Code de la
            consommation. La validation de l'inscription emporte acceptation pleine et entière de cette information ; la
            date d'acceptation des présentes CGV est horodatée et conservée par la Plateforme à titre de preuve.
          </p>
          <p>
            Les modalités d'annulation, de modification, de force majeure et de remboursement des inscriptions sont
            régies par <strong className='text-slate-900'>l'article 8</strong> ci-après et par les conditions
            particulières propres à chaque Concours, fixées par son Organisateur.
          </p>
          <p className='pt-3'>
            <strong className='text-slate-900 text-base'>7.3 Personnes morales et professionnels</strong>
          </p>
          <p>
            Le droit de rétractation prévu aux articles L.221-18 et suivants du Code de la consommation ne bénéficie pas
            aux personnes morales (associations, clubs, sociétés) ni aux personnes physiques agissant dans le cadre de
            leur activité professionnelle. Les Organisateurs (plan Commission Partenaire ou Forfait) et les Clubs
            souscrivant au plan Club Pro dans le cadre de leur activité associative ou professionnelle sont expressément
            exclus du présent dispositif.
          </p>
        </div>
      </div>

      {/* 8. Remboursements et annulations */}
      <div id='remboursements' className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
        <h2 className='text-xl font-extrabold text-slate-900 mb-4'>
          8. Remboursements et politique d'annulation des inscriptions
        </h2>
        <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
          <p>
            Les droits d'inscription versés par un Joueur pour participer à un Concours payant sont encaissés
            directement par l'Organisateur via Stripe Connect (article 6). La politique de remboursement de ces sommes
            relève de la responsabilité principale de l'Organisateur, qui doit la communiquer dans la description de son
            Concours, sous le contrôle des dispositions impératives suivantes.
          </p>
          <p>
            <strong className='text-slate-900'>8.1 Annulation totale du Concours par l'Organisateur :</strong> en cas
            d'annulation d'un Concours après encaissement des droits d'inscription, l'Organisateur est tenu de
            rembourser intégralement les Joueurs inscrits dans un délai maximal de 14 jours suivant la décision
            d'annulation. Aucune retenue ne peut être pratiquée par l'Organisateur, sous réserve de l'absence de fraude
            imputable au Joueur. Le défaut de remboursement dans ce délai peut entraîner, sans préavis, la suspension du
            compte Organisateur sur la Plateforme et l'inscription du litige au registre des incidents prévu à l'article
            9.
          </p>
          <p>
            <strong className='text-slate-900'>8.2 Modification substantielle du Concours :</strong> en cas de
            modification substantielle apportée au Concours par l'Organisateur après l'inscription du Joueur — notamment
            changement de date, de lieu (commune), de format de jeu, de catégorie d'éligibilité ou de prix —
            l'Organisateur en informe les Joueurs inscrits par email dans les plus brefs délais. Chaque Joueur dispose
            alors d'un délai de 7 jours calendaires pour demander le remboursement intégral de ses droits d'inscription,
            sans frais ni pénalité. À défaut de demande dans ce délai, le Joueur est réputé accepter la modification.
          </p>
          <p>
            <strong className='text-slate-900'>8.3 Force majeure et causes externes :</strong> en cas d'annulation, de
            report ou d'interruption du Concours pour un motif de force majeure au sens de l'article 1218 du Code civil
            (notamment : interdiction ou prescription préfectorale, intempéries rendant le terrain impraticable, crise
            sanitaire, défaillance grave d'un prestataire essentiel), l'Organisateur procède au remboursement intégral
            des droits d'inscription pour les prestations non effectuées. L'Organisateur peut, à titre alternatif et
            avec l'accord exprès du Joueur, proposer un report de l'inscription sur une date ultérieure ; à défaut
            d'accord du Joueur, le remboursement intégral demeure dû.
          </p>
          <p>
            <strong className='text-slate-900'>8.4 Désistement volontaire du Joueur :</strong> en dehors des cas visés
            en 8.1, 8.2 et 8.3, et sans préjudice de l'absence de droit de rétractation rappelée à l'article 7.2, les
            conditions de remboursement en cas de désistement volontaire du Joueur sont définies par l'Organisateur dans
            la description de son Concours. En l'absence de mention expresse, aucun remboursement n'est dû par
            l'Organisateur en cas de désistement volontaire.
          </p>
          <p>
            <strong className='text-slate-900'>8.5 Non-présentation (no-show) :</strong> en cas de non-présentation du
            Joueur ou d'une équipe au Concours, sans annulation préalable communiquée à l'Organisateur dans les délais
            et formes prévus par les conditions particulières du Concours, aucun remboursement n'est dû. La place
            réservée n'est pas restituée et reste due par le Joueur.
          </p>
          <p>
            <strong className='text-slate-900'>8.6 Modalités techniques du remboursement :</strong> les remboursements
            sont opérés exclusivement via Stripe Connect, par recrédit sur le moyen de paiement initial du Joueur. Les
            délais effectifs de mise à disposition des fonds dépendent du réseau bancaire du Joueur et restent étrangers
            à la responsabilité de MADSAAS SAS. Les remboursements, contestations (rétrofacturations) et frais associés
            peuvent générer des frais bancaires ou Stripe ; l'Organisateur en demeure seul redevable. En cas de litige
            bancaire (chargeback) imputable à un Joueur ayant pourtant participé au Concours, l'Organisateur peut
            produire à Stripe la preuve de la prestation ainsi que la preuve datée de l'acceptation des présentes CGV
            par le Joueur (article 7.2), conservée par la Plateforme.
          </p>
          <p>
            <strong className='text-slate-900'>8.7 Tiers facilitateur — défaut de l'Organisateur :</strong> en cas de
            défaut prolongé de remboursement imputable à l'Organisateur (annulation visée en 8.1, ou cas de force
            majeure visé en 8.3 sans remboursement effectif au-delà de 30 jours), MADSAAS SAS peut, à titre exceptionnel
            et sans obligation de résultat, intervenir comme tiers facilitateur : mise en relation, mise en demeure de
            l'Organisateur, déclenchement d'un reversement de la commission BoulesConnect au bénéfice du Joueur si les
            fonds correspondants ont été retenus par Stripe sur le compte de la Plateforme, ou suspension du compte
            Organisateur. Cette intervention ne fait pas de MADSAAS SAS un débiteur des sommes initialement versées à
            l'Organisateur, qui demeurent à la charge exclusive de ce dernier.
          </p>
          <p>
            <strong className='text-slate-900'>8.8 Commission BoulesConnect :</strong> la commission (
            {COMMISSION_SHORT_LABEL} par inscription) perçue par MADSAAS SAS sur chaque inscription payante (article
            4.3) n'est pas remboursable en cas de désistement volontaire du Joueur (8.4) ni de non-présentation (8.5).
            Elle est restituée au Joueur en cas d'annulation totale par l'Organisateur (8.1), de modification
            substantielle ouvrant droit à remboursement (8.2) ou de force majeure (8.3).
          </p>
          <p>
            <strong className='text-slate-900'>8.9 Médiation et voies de recours :</strong> en cas de litige relatif à
            un remboursement non résolu directement entre le Joueur et l'Organisateur (ou avec MADSAAS SAS lorsque la
            Plateforme est directement concernée), le Joueur consommateur peut recourir gratuitement au service de
            médiation de la consommation désigné à l'article 14, ou à la plateforme européenne de règlement en ligne des
            litiges (RLL).
          </p>
        </div>
      </div>
    </>
  );
}
